import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';

import { checkExistingMember } from 'api/intake';
import { fetchReliveOptions } from 'api/homelesses';

import {
    DebitCard,
    DebitCardOptions,
    Deployments,
    Homeless,
    MemberSearchResult,
    TouchpointQuestions,
    User
} from 'types';

import { useHistory } from 'react-router-dom';
import Account from './Account';
import Alert from './Alert';
import CongratsPage from './CongratsPage';
import ExitIntake from 'components/_shared/PopUp2/components/ExitIntake';
import Guide from './Guide';
import IntakeHeader from './Header';
import LoadingCircle from '../_shared/LoadingCircle';
import Modal from '../_shared/Modal';
import PopUp2 from '../_shared/PopUp2';
import StatusCapture from './StatusCapture';
import TargetList from '../_shared/PopUp2/components/TargetList';
import TermsOfService from './terms_of_service';

import AuthManager from '../../auth';
import { useRemoteCopy } from 'hooks';

import Button from '@mui/material/Button';
import { makeStyles } from '@mui/styles';
import { ExtensionRequestKind, IntakeMemberSearchResultHelper, IntakeMemberSearchResultType, RELIVE_OPTIONS_UNAVAILABLE_REASON, resolveInactiveMembershipResult } from 'util/membershipHelper';
import { parseApiErrorMessage } from 'util/parseApiErrorMessage';
import ExistingRestrictedMember from 'components/membership/ExistingRestrictedMember';
import ReenrollPopup from 'components/membership/ReenrollPopup';
import ReinstatePopup from 'components/membership/ReinstatePopup';
import ReinstateForm from 'components/membership/ReinstateForm';
import DirectReinstatePopup from 'components/membership/DirectReinstatePopup';
import ExistingMember from 'components/membership/ExistingMember';
import ClosedMembershipPopup from 'components/membership/ClosedMembershipPopup';

enum ModalType {
    None = "NONE",
    TargetOrEligible = "TARGET_OR_ELIGIBLE",
    Active = "ACTIVE",
    ActiveRestrictedView = "ACTIVE_RESTRICTED_VIEW",
    Reenroll = "REENROLL",
    Reinstate = "REINSTATE",
    ReinstateDirect = "REINSTATE_DIRECT",
    ReinstateForm = "REINSTATE_FORM",
    Closed = "CLOSED",
}

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100vh',
        overflow: 'auto',
        width: '100%'
    },
    alert: {
        position: 'fixed',
        top: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.5)',
        height: '100vh',
        width: 'calc(100vw - 300px)',
        zIndex: 3,
        '@media (max-width: 768px)': {
            left: 0,
            width: '100vw'
        }
    },
    submitOverlay: {
        position: 'fixed',
        top: 0,
        right: 0,
        zIndex: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.65)',
        height: '100vh',
        width: 'calc(100vw - 300px)',
        '@media (max-width: 768px)': {
            left: 0,
            width: '100vw'
        }
    },
    existingMemberBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '32px'
    },
    existingMemberButtonContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginTop: '40px'
    },
    guideBtn: {
        position: 'fixed',
        zIndex: 2,
        bottom: 30,
        right: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#FFFFFF',
        border: '1px solid #FFFFFF80',
        borderRadius: '50px',
        boxShadow: '5px 6px 20px 0px #00000033',
        color: '#7378E8',
        fontSize: '24px',
        height: '64px',
        marginRight: '16px',
        minWidth: '64px !important'
    },
    // detailLabel: {
    //     color: '#5A6A81',
    //     fontWeight: 300,
    //     fontSize: '16px'
    // },
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '32px'
    }
});

type IntakeProps = {
    ctpEmail: string;
    ctpId: number;
    ctpName: string;
    user: User;
    ctpPartner: User['partner'];
    decrementStep: () => void;
    debitCardOptions: DebitCardOptions[];
    debitInfo: DebitCard;
    deployments: Deployments[]; // check this type
    deployment_id: number;
    fromIntakeAutofill: boolean;
    getApiError: boolean;
    getApiErrorResponse: { error?: string; message?: string; text?: string };
    getDebitCardOptions: () => void;
    getTouchpointQuestions: (token: string) => void;
    incrementStep: () => void;
    homelesses: Homeless[];
    intakeAgreed: boolean;
    intakeBeaconId: string;
    intakeBirthDay: string;
    intakeDeploymentId: number;
    intakeEmail: string;
    intakeGoals: any[];
    intakeGuideMessages: any[]; // check this type
    intakeGuideSteps: any[]; // check this type
    intakeIcebreaker: string;
    intakeId: number;
    intakeIsSubmitted: boolean;
    intakeIsSubmitting: boolean;
    intakeNeeds: any[]; // check this type
    intakePhoneNo: string;
    intakePrivateFirstName: string;
    intakePrivateFullName: string;
    intakePrivateLastName: string;
    intakePrivateMiddleName: string;
    intakeProfileName: string;
    intakeProfilePic: string;
    intakeProgressWidth: { step: number; width: number }[];
    intakeQuestionsAndAnswers: any[]; // check this type
    intakeLanguagePreference: string;
    intakeRelevantId: string;
    intakeStep: number;
    intakeTouchpointIsSubmitted: boolean;
    intakeTouchpointIsSubmitting: boolean;
    intakeTouchpoints: any[]; // check this type
    intake_steps: string[];
    isFetchingTouchpointQuestions: boolean;
    is_showing_intake_guides: boolean;
    layoutTitles: {
        title: string;
        subtitle: string;
    };
    maxIntakeStep: number;
    members_enrolled: number;
    minIntakeStep: number;
    orgId: number;
    resetError: () => void;
    resetIntake: () => void;
    setIntakeBeaconId: (beaconId: string) => void;
    setIntakeBirthDay: (birthDay: string) => void;
    setIntakeDeploymentId: (deploymentId: number) => void;
    setIntakeEmail: (email: string) => void;
    setIntakeIcebreaker: (icebreaker: string) => void;
    setIntakePhoneNo: (phoneNo: string) => void;
    setIntakePrivateFirstName: (firstName: string) => void;
    setIntakePrivateFullName: (fullName: string) => void;
    setIntakePrivateLastName: (lastName: string) => void;
    setIntakePrivateMiddleName: (middleName: string) => void;
    setIntakeProfileName: (profileName: string) => void;
    setIntakeProfilePicUrl: (url: string | null) => void;
    setIntakeRelevantId: (relevantId: string) => void;
    setIntakeLanguagePreference: (languagePreference: string) => void;
    setIntakeTouchpoints: (questionId: number, answer: string) => void;
    setLayoutTitles: (title: string, subtitle: string) => void;
    setViewGuideMessage: (message: string) => void;
    submitIntake: (formData: any, token: string) => void;
    submitIntakeTouchpoint: (formData: any, token: string) => void;
    toggleIntakeAgreed: () => void;
    touchpointQuestions: TouchpointQuestions[]; // check this type
    updateUserSettings: (settings: any) => void;
};

// TODO: REFACTOR.
const Intake: React.FC<IntakeProps> = ({
    ctpEmail,
    ctpId,
    ctpName,
    ctpPartner,
    user,
    debitCardOptions,
    debitInfo,
    decrementStep,
    deployments,
    deployment_id,
    fromIntakeAutofill,
    getApiError,
    getApiErrorResponse,
    getDebitCardOptions,
    getTouchpointQuestions,
    homelesses,
    incrementStep,
    intakeAgreed,
    intakeBeaconId,
    intakeBirthDay,
    intakeDeploymentId,
    intakeEmail,
    intakeGoals,
    intakeGuideMessages,
    intakeGuideSteps,
    intakeIcebreaker,
    intakeId,
    intakeIsSubmitted,
    intakeIsSubmitting,
    intakePhoneNo,
    intakePrivateFirstName,
    intakePrivateFullName,
    intakePrivateLastName,
    intakePrivateMiddleName,
    intakeProfileName,
    intakeProfilePic,
    intakeProgressWidth,
    intakeQuestionsAndAnswers,
    intakeRelevantId,
    intakeLanguagePreference,
    intakeStep,
    intakeTouchpointIsSubmitted,
    intakeTouchpointIsSubmitting,
    intakeTouchpoints,
    intake_steps,
    isFetchingTouchpointQuestions,
    is_showing_intake_guides,
    layoutTitles,
    maxIntakeStep,
    members_enrolled,
    minIntakeStep,
    orgId,
    resetError,
    resetIntake,
    setIntakeBeaconId,
    setIntakeBirthDay,
    setIntakeDeploymentId,
    setIntakeEmail,
    setIntakeIcebreaker,
    setIntakePhoneNo,
    setIntakePrivateFirstName,
    setIntakePrivateFullName,
    setIntakePrivateLastName,
    setIntakePrivateMiddleName,
    setIntakeProfileName,
    setIntakeProfilePicUrl,
    setIntakeRelevantId,
    setIntakeLanguagePreference,
    setIntakeTouchpoints,
    setLayoutTitles,
    setViewGuideMessage,
    submitIntake,
    submitIntakeTouchpoint,
    toggleIntakeAgreed,
    touchpointQuestions,
    updateUserSettings
}) => {
    const classes = useStyles();
    const history = useHistory();
    const AuthInstance = AuthManager.getInstance();

    const lastCheckedMemberValues = useRef({
        intakeBirthDay: '',
        intakePrivateFirstName: '',
        intakePrivateLastName: '',
        intakeRelevantId: ''
    });

    const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);

    const valuesChanged =
        lastCheckedMemberValues.current.intakeRelevantId !== intakeRelevantId ||
        lastCheckedMemberValues.current.intakeBirthDay !== intakeBirthDay ||
        lastCheckedMemberValues.current.intakePrivateFirstName !==
            intakePrivateFirstName ||
        lastCheckedMemberValues.current.intakePrivateLastName !==
            intakePrivateLastName ||
        (intakeRelevantId && !lastCheckedMemberValues.current.intakeRelevantId);

    const refetchCount = useRef(0);

    const remoteIntake = useRemoteCopy(`intake`);

    const intakeSteps = useMemo(
        () =>
            intake_steps ? intake_steps : AuthInstance.getIntakeSteps() || [],
        [intake_steps, AuthInstance]
    );

    // Only block the whole tree on initial remote-copy load. Submit loading
    // uses an overlay so Guide/ToS stay mounted (avoids re-prompting the guide).
    const isInitialLoading = !remoteIntake.isLoaded;
    const isSubmitting = intakeIsSubmitting || intakeTouchpointIsSubmitting;

    //
    // State
    //
    const [prefilled, setPrefilled] = useState(false);

   
    const [existingMember, setExistingMember] =
        useState<null | MemberSearchResult>(null);
    const [modalType, setModalType] = useState<ModalType>(ModalType.None);
    const [unavailableReason, setUnavailableReason] = useState<string | null>(null);
    const [extensionRequestKind, setExtensionRequestKind] =
        useState<ExtensionRequestKind>('reinstatement');
    const [fromFoundMember, setFromFoundMember] = useState(false);


    const [exitIntake, setExitIntake] = useState(false);
    const [confirmTargetList, setConfirmTargetList] = useState(false);
    const [guide, setGuide] = useState(
        is_showing_intake_guides ? is_showing_intake_guides : false
    );

    // const [hasCheckedTargetList, setHasCheckedTargetList] = useState(false);
    const [hasDismissedTargetList, setHasDismissedTargetList] = useState(false);
    const [isErrored, setIsErrored] = useState(false);
    const [progressBarWidth, setProgressBarWidth] = useState(
        intakeProgressWidth ? intakeProgressWidth[intakeStep]?.width : 0
    );
    const [state, setState] = useState({
        showAlert: false,
        alertText: ''
    });
    const [toggleGuide, setToggleGuide] = useState(false);
    const intakePublicName = useMemo(() => intakeProfileName || intakePrivateFirstName, [intakeProfileName, intakePrivateFirstName]);
    const deployment = deployments.find(d => d.id === intakeDeploymentId);

    //
    // Alert
    //
    const closeAlert = () => {
        setState({
            ...state,
            alertText: '',
            showAlert: false
        });

        resetError();
    };

    const setAlert = useCallback(
        (text: string) => {
            setState({
                ...state,
                alertText: text,
                showAlert: true
            });
        },
        [state]
    );

    const alertComponent = () => {
        if (!state.showAlert) return null;
        return (
            <div className={classes.alert} onClick={closeAlert}>
                <Alert text={state.alertText} />
            </div>
        );
    };

    //
    // Component Props
    //
    const shortIntake = useMemo(
        () =>
            Array.isArray(intakeSteps)
                ? intakeSteps.filter(
                      (step: string) =>
                          step !== 'Needs' && step !== 'Public Info' && step !== 'Action Steps'
                  )
                : [],
        [intakeSteps]
    );
    
    const accountProps = {
        prefilled,
        existingMember,
        deployments,
        user,
        incrementStep,
        intakeBirthDay,
        intakeDeploymentId,
        intakeEmail,
        intakePhoneNo,
        intakePrivateFirstName,
        intakePrivateFullName,
        intakePrivateLastName,
        intakePrivateMiddleName,
        intakeProfileName,
        intakeProfilePic,
        profilePictureFile,
        intakeLanguagePreference,
        intakeRelevantId,
        progressBarWidth,
        remoteCopy: remoteIntake,
        setGuide,
        setIntakeBirthDay,
        setIntakeDeploymentId,
        setIntakeEmail,
        setIntakePhoneNo,
        setIntakePrivateFirstName,
        setIntakePrivateFullName,
        setIntakePrivateLastName,
        setIntakePrivateMiddleName,
        setIntakeProfileName,
        setIntakeProfilePicUrl,
        setProfilePictureFile,
        setIntakeRelevantId,
        setIntakeLanguagePreference
    };

    const congratsProps = {
        debitCardOptions,
        debitInfo,
        homelesses,
        intakeEmail,
        intakeId,
        intakeProfilePic,
        memberName: intakePublicName
    };

    const guideIntakeProps = {
        ctpId,
        ctpName,
        ctpPartner,
        intakeGuideMessages,
        intakeGuideSteps,
        intakeId,
        intakeIsSubmitted,
        intakePublicName,
        intakeStep,
        intakeSteps: shortIntake,
        intakeTouchpointIsSubmitted,
        members_enrolled,
        minIntakeStep,
        guide,
        setGuide,
        setToggleGuide,
        setViewGuideMessage,
        toggleGuide,
        updateUserSettings
    };

    const headerStatusProps = {
        intakeAgreed,
        intakeBirthDay,
        intakeDeploymentId,
        intakeEmail,
        intakePhoneNo,
        intakePrivateFullName,
        intakeProfileName: intakePublicName,
        intakeProfilePic,
        intakeProgressWidth,
        intakeRelevantId,
        intakeTouchpoints,
        is_showing_intake_guides,
        progressBarWidth,
        setProgressBarWidth,
        touchpointQuestions:
            touchpointQuestions?.filter(
                (question: TouchpointQuestions) =>
                    question.type === 'StatusQuestion'
            ) || []
    };

    const statusCaptureProps = {
        incrementStep,
        intakeTouchpoints,
        progressBarWidth,
        setIntakeTouchpoints,
        touchpointQuestions
    };

    const termsOfServiceProps = {
        agreed: intakeAgreed,
        answers: intakeQuestionsAndAnswers,
        beaconId: intakeBeaconId,
        birthDay: intakeBirthDay,
        deploymentId: intakeDeploymentId ? intakeDeploymentId : deployment_id,
        email: intakeEmail,
        firstName: intakePrivateFirstName,
        goals: intakeGoals,
        icebreaker: intakeIcebreaker,
        id: intakeId,
        intakeTouchpoints,
        interviewer: ctpEmail,
        interviewerId: ctpId,
        isSubmitted: intakeIsSubmitted,
        isSubmitting: intakeIsSubmitting,
        lastName: intakePrivateLastName,
        middleName: intakePrivateMiddleName,
        orgId,
        orgName: ctpPartner?.name ? ctpPartner.name : '',
        phoneNo: intakePhoneNo,
        profileName: intakePublicName,
        profilePic: intakeProfilePic,
        profilePictureFile,
        relevantId: intakeRelevantId,
        languagePreference: intakeLanguagePreference,
        setIntakeIcebreaker,
        submit: submitIntake,
        submitTouchpoint: submitIntakeTouchpoint,
        toggleAgreed: toggleIntakeAgreed,
        touchpointIsSubmitted: intakeTouchpointIsSubmitted,
        touchpointIsSubmitting: intakeTouchpointIsSubmitting
    };

    //
    // Functions
    //
    const callback = useCallback(() => {
        const token = AuthInstance.getToken();

        if (
            !isFetchingTouchpointQuestions &&
            token &&
            touchpointQuestions.length === 0
        ) {
            getTouchpointQuestions(token);
        }
    }, [
        AuthInstance,
        getTouchpointQuestions,
        isFetchingTouchpointQuestions,
        touchpointQuestions.length
    ]);

    const dynamicIntake = () => {
        if (!shortIntake || shortIntake.length === 0) {
            return [
                <Account {...accountProps} />,
                <StatusCapture {...statusCaptureProps} />
            ];
        }

        const intakeBody = shortIntake?.map((step: string) => {
            switch (step) {
                case `Private Info`:
                    return <Account {...accountProps} />;
                case `Status Capture`:
                    return <StatusCapture {...statusCaptureProps} />;
                default:
                    return null;
            }
        });

        return intakeBody;
    };

    const handleExitIntake = () => {
        setExitIntake(true);
    };

    const fillIntakeInfo = (
        foundMember: MemberSearchResult
    ) => {
        const formatPhone = (phone: string) => {
            const removeRegionCode = phone.replace('+1', '');

            if (removeRegionCode.length === 10) {
                return `(${removeRegionCode.slice(
                    0,
                    3
                )}) ${removeRegionCode.slice(3, 6)}-${removeRegionCode.slice(
                    6,
                    removeRegionCode.length
                )}`;
            }

            return removeRegionCode;
        };

        const formattedPhone = foundMember.phone
            ? formatPhone(foundMember.phone)
            : '';

        type Mapping = [(value: string) => void, string];

        const mappings: Mapping[] = [
            [setIntakeBirthDay, foundMember.date_of_birth || ''],
            [setIntakeProfileName, foundMember.name],
            [setIntakeProfilePicUrl, foundMember.photo],
            [setIntakePrivateFirstName, foundMember.pii_first_name || ''],
            [setIntakePrivateLastName, foundMember.pii_last_name || ''],
            [setIntakePrivateFullName, `${foundMember.pii_first_name || ''} ${foundMember.pii_last_name || ''}`],
            [setIntakeLanguagePreference, foundMember.language_preference || ''],
            [setIntakeEmail, foundMember.email || ''],
            [setIntakePhoneNo, formattedPhone],
        ];

        //only set intakeRelevantId if the member is target or eligible
        if (foundMember.profile_stage === 'TARGET_LIST' || foundMember.profile_stage === 'ELIGIBILITY_LIST') {
            mappings.push([setIntakeRelevantId, foundMember.pii_member_id || ''])
            setIntakeBeaconId(foundMember.beacon_id)
        }

        mappings.forEach(([setter, newValue]) => setter(newValue));
        // autofillIntake(foundMember);
    }

    const handleFillExistingMember = () => {
        if (!existingMember) return;

        setFromFoundMember(true);
        fillIntakeInfo(existingMember);
        setPrefilled(true);
    }

    const handleFillTargetList = () => {
        if (!existingMember) return;

        fillIntakeInfo(existingMember);
        setPrefilled(true);

        setConfirmTargetList(true);
        setHasDismissedTargetList(true);
        setModalType(ModalType.None);
    };

    const handleRejectTargetList = () => {
        setExistingMember(null);
        // setHasCheckedTargetList(false);
        setHasDismissedTargetList(true);
        setExistingMember(null);
        setModalType(ModalType.None);
    };

    const handleCreateNewMembership = () => {
        if (!existingMember) return;

        setModalType(ModalType.None);
        handleFillExistingMember();
    };

    const closeIntake = () => {
        resetIntake();
        history.push('/');
    };

    const getExistingMemberPopUp = () => {
        if (!existingMember) return null;
        switch (modalType) {
            case ModalType.TargetOrEligible:
                return (
                    <PopUp2
                        onClose={() => {
                            setExistingMember(null);
                            setModalType(ModalType.None);
                        }}
                        open={existingMember !== null}
                        title="Target List Member"
                    >
                        <TargetList
                            member={existingMember}
                            onAccept={handleFillTargetList}
                            onReject={handleRejectTargetList}
                        />
                    </PopUp2>
                );
            case ModalType.Active:
                return (
                    <ExistingMember
                        member={existingMember}
                        closeButtonText="Back to Homepage"
                        handleCloseClick={() => { 
                            setModalType(ModalType.None); 
                            closeIntake(); 
                        }}
                    />
                );
            case ModalType.ActiveRestrictedView:
                return (
                    <ExistingRestrictedMember
                        member={existingMember}
                        closeButtonText="Back to Homepage"
                        handleCloseClick={() => { 
                            setModalType(ModalType.None); 
                            closeIntake(); 
                        }}
                    />
                );
            case ModalType.Reenroll:
                return (
                    <ReenrollPopup
                        member={existingMember}
                        closeButtonText="Back to Homepage"
                        handleCloseClick={() => { 
                            setModalType(ModalType.None); 
                            closeIntake(); 
                        }}
                        handleReenroll={() => {
                            handleCreateNewMembership();
                            setModalType(ModalType.None);
                        }}
                    />
                );
            case ModalType.Reinstate:
                return (
                    <ReinstatePopup
                        member={existingMember}
                        closeButtonText="Back to Homepage"
                        handleCloseClick={() => { 
                            setModalType(ModalType.None); 
                            closeIntake(); 
                        }}
                        handleReinstate={() => {
                            setModalType(ModalType.ReinstateForm);
                        }}
                    />
                );
            case ModalType.ReinstateDirect:
                return (
                    <DirectReinstatePopup
                        member={existingMember}
                        closeButtonText="Back to Homepage"
                        onClose={() => {
                            setModalType(ModalType.None);
                            closeIntake();
                        }}
                        onFinish={() => {
                            setModalType(ModalType.None);
                            closeIntake();
                        }}
                    />
                );
            case ModalType.ReinstateForm:
                return (
                    <ReinstateForm
                        member={existingMember}
                        deploymentName={deployment?.name}
                        requestKind={extensionRequestKind}
                        closeButtonText="Back to Homepage"
                        onClose={() => {
                            setModalType(ModalType.None);
                            closeIntake();
                        }}
                        onFinish={() => {
                            setModalType(ModalType.None);
                            closeIntake();
                            //uncomment this when we have a way to redirect to the holder profile
                            //history.push(`/holders/${existingMember.id}`);
                        }}
                    />
                );
            case ModalType.Closed:
                return (
                    <ClosedMembershipPopup
                        member={existingMember}
                        reason={unavailableReason}
                        closeButtonText="Back to Homepage"
                        handleCloseClick={() => {
                            setModalType(ModalType.None);
                            closeIntake();
                        }}
                    />
                );
            default:
                return null;
        }
    }

    //
    // Use Effects
    //
    useEffect(() => {
        const handleResponse = async (response: MemberSearchResult[]) => {
            if (!deployment) return;
            let intakeMemberSearchResult = IntakeMemberSearchResultHelper.getIntakeMemberSearchResultType(response, deployment);
            if (intakeMemberSearchResult.memberResult === null) {
                return;
            }

            // Intake search on private info. Same relive_options rules as pre-intake.
            // Direct reinstate calls reinstate_request with no body. Max duration opens the extension form.
            if (
                intakeMemberSearchResult.type === IntakeMemberSearchResultType.reenroll ||
                intakeMemberSearchResult.type === IntakeMemberSearchResultType.reinstate
            ) {
                try {
                    const reliveOptions = await fetchReliveOptions(intakeMemberSearchResult.memberResult.id);
                    intakeMemberSearchResult = resolveInactiveMembershipResult(
                        intakeMemberSearchResult.memberResult,
                        deployment.id,
                        reliveOptions
                    );
                } catch (reliveError) {
                    console.error('Error fetching relive options:', reliveError);
                    intakeMemberSearchResult = {
                        type: IntakeMemberSearchResultType.closed,
                        memberResult: intakeMemberSearchResult.memberResult,
                        unavailableReason: RELIVE_OPTIONS_UNAVAILABLE_REASON
                    };
                }
            }

            if (intakeMemberSearchResult.memberResult === null) {
                return;
            }

            setExistingMember(intakeMemberSearchResult.memberResult);
            setUnavailableReason(intakeMemberSearchResult.unavailableReason ?? null);
            // Map IntakeMemberSearchResultType to ModalType
            switch (intakeMemberSearchResult.type) {
                case IntakeMemberSearchResultType.targetOrEligible:
                    setModalType(ModalType.TargetOrEligible);
                    break;
                case IntakeMemberSearchResultType.active:
                    setModalType(ModalType.Active);
                    break;
                case IntakeMemberSearchResultType.activeRestrictedView:
                    setModalType(ModalType.ActiveRestrictedView);
                    break;
                case IntakeMemberSearchResultType.reenroll:
                    setModalType(ModalType.Reenroll);
                    break;
                case IntakeMemberSearchResultType.reinstate:
                    setExtensionRequestKind(
                        intakeMemberSearchResult.extensionRequestKind ?? 'reinstatement'
                    );
                    setModalType(ModalType.Reinstate);
                    break;
                case IntakeMemberSearchResultType.reinstateDirect:
                    setModalType(ModalType.ReinstateDirect);
                    break;
                case IntakeMemberSearchResultType.closed:
                    setModalType(ModalType.Closed);
                    break;
                default:
                    setModalType(ModalType.None);
            }
        };

        const checkMember = async () => {
            try {
                const response = await checkExistingMember({
                    memberID: intakeRelevantId.trim(),
                    dob: intakeBirthDay,
                    memberFirstName: intakePrivateFirstName,
                    memberLastName: intakePrivateLastName
                });

                lastCheckedMemberValues.current = {
                    intakeBirthDay,
                    intakePrivateFirstName,
                    intakePrivateLastName,
                    intakeRelevantId
                };

                await handleResponse(response);
            } catch (error) {
                console.error('Error checking target list:', error);
            }
        };

        const hasInsuranceID =
            intakeRelevantId &&
            !intakeBirthDay &&
            !intakePrivateFirstName &&
            !intakePrivateLastName;
        const hasNameDOB =
            intakeBirthDay &&
            intakePrivateFirstName &&
            intakePrivateLastName &&
            !intakeRelevantId;

        if (
            shortIntake[intakeStep - 1] === 'Private Info' &&
            !confirmTargetList &&
            !hasDismissedTargetList &&
            (hasInsuranceID || hasNameDOB || intakeRelevantId) &&
            valuesChanged &&
            !fromIntakeAutofill &&
            !fromFoundMember
        ) {
            checkMember();
        }
    }, [
        fromFoundMember,
        confirmTargetList,
        fromIntakeAutofill,
        hasDismissedTargetList,
        intakeBirthDay,
        intakePrivateFirstName,
        intakePrivateLastName,
        intakeRelevantId,
        intakeStep,
        shortIntake,
        valuesChanged,
        deployment
    ]);

    useEffect(() => {
        if (valuesChanged) {
            setHasDismissedTargetList(false);
        }
    }, [
        intakeBirthDay,
        intakePrivateFirstName,
        intakePrivateLastName,
        intakeRelevantId,
        valuesChanged
    ]);

    // useEffect(() => {
    //     setHasDismissedTargetList(false);
    // }, [
    //     intakeBirthDay,
    //     intakePrivateFirstName,
    //     intakePrivateLastName,
    //     intakeRelevantId
    // ]);

    useEffect(() => {
        if (
            layoutTitles.title !== '' ||
            layoutTitles.subtitle !== 'Member Intake'
        ) {
            setLayoutTitles('', 'Member Intake');
        }
    }, [
        layoutTitles.subtitle,
        layoutTitles.title,
        resetIntake,
        setLayoutTitles
    ]);

    useEffect(() => {
        if (
            !state.showAlert &&
            getApiError &&
            getApiErrorResponse !== state.alertText &&
            intakeStep !== maxIntakeStep
        ) {
            const alertMessage = parseApiErrorMessage(getApiErrorResponse);
            setAlert(alertMessage);
        }
    }, [
        getApiError,
        getApiErrorResponse,
        intakeStep,
        maxIntakeStep,
        setAlert,
        state.alertText,
        state.showAlert
    ]);

    useEffect(() => {
        if (!debitCardOptions) {
            getDebitCardOptions();
        }
    }, [debitCardOptions, getDebitCardOptions]);

    useEffect(() => {
        if (remoteIntake.isError && refetchCount.current < 5) {
            refetchCount.current++;
            remoteIntake.refetch();
        }

        if (refetchCount.current >= 5) {
            setIsErrored(true);
        }
    }, [remoteIntake]);

    const bodyComponents = [
        ...dynamicIntake(),
        <TermsOfService {...termsOfServiceProps} />,
        <CongratsPage {...congratsProps} />
    ];

    if (isErrored) {
        return (
            <Modal open={isErrored}>
                <div style={{ padding: 20 }}>
                    <div className="font-lg">Something has gone wrong.</div>
                    <div>
                        Please refresh and try again. We apologize for this
                        inconvenience. If this error persists{' '}
                        <a href="mailto:support@samaritan.com">
                            contact support
                        </a>
                    </div>
                </div>
            </Modal>
        );
    }

    if (isInitialLoading) {
        return (
            <div style={{ height: '100%' }}>
                <LoadingCircle />
            </div>
        );
    }

    return (
        <div ref={callback} className={classes.root} id="intake-container">
            <Helmet title="Member Registration" />
            {isSubmitting && (
                <div className={classes.submitOverlay}>
                    <LoadingCircle />
                </div>
            )}
            {(is_showing_intake_guides || toggleGuide) &&
                intakeStep !== maxIntakeStep && (
                    <Guide {...guideIntakeProps} />
                )}
            {intakeStep < maxIntakeStep && (
                <IntakeHeader
                    decrementStep={decrementStep}
                    handleExitIntake={handleExitIntake}
                    intakeSteps={shortIntake}
                    maxStep={maxIntakeStep}
                    minStep={minIntakeStep}
                    step={intakeStep}
                    {...headerStatusProps}
                />
            )}
            {alertComponent()}
            {bodyComponents[intakeStep - 1]}
            {!is_showing_intake_guides && (
                <Button
                    className={classes.guideBtn}
                    onClick={() => setToggleGuide(true)}
                    aria-label="Open intake guide"
                >
                    <i className="fas fa-question" />
                </Button>
            )}
            {existingMember && modalType !== ModalType.None && (
                getExistingMemberPopUp()
            )}
            {exitIntake && (
                <PopUp2
                    onClose={() => setExitIntake(false)}
                    open={exitIntake}
                    title="Exit Intake"
                >
                    <ExitIntake onClose={() => setExitIntake(false)} />
                </PopUp2>
            )}
        </div>
    );
};

export default Intake;
